import {
    Component,
    OnInit,
    Output,
    EventEmitter,
    ElementRef,
    HostListener,
    OnDestroy
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { NavigationEnd, Router, RouterModule } from '@angular/router'

import { TranslateModule, TranslateService } from '@ngx-translate/core'

import { LOCAL_STORAG_KEYS } from '@@constants/local-storage-keys'
import { SECURITY_TAGS } from '@@constants/security-tags'
import { buildPortalModules } from '@@constants/portal-modules'

import getBoolean from '@@utils/getBoolean'

import { SharedModule } from '@@shared/shared.module'

import { filter, Subscription } from 'rxjs'

import { FingerPrintService } from '@@services/fingerprint.service'
import { GeneralService } from '@@services/general.service'
import { AuthService } from '@@services/auth.service'
import { AlertService } from '@@services/alert.service'

import { CUSTOM_EVENTS } from '@@constants/custom-events'

import { BusinessSwitcherComponent } from '../business-switcher/business-switcher.component'

@Component({
    standalone: true,
    imports: [
        SharedModule,
        CommonModule,
        RouterModule,
        TranslateModule,
        BusinessSwitcherComponent
    ],
    selector: 'in-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss']
})
export class SidenavComponent implements OnInit, OnDestroy {
    @Output() toggleMenu: EventEmitter<any> = new EventEmitter()

    public app: any

    hide: boolean = true
    hide2: boolean = true
    hide3: boolean = true
    hasFPrint: boolean = false
    isReceptionist: boolean = false
    companyDetails: any
    companyDetailsConstruct: any
    canViewEvents: boolean = false
    canViewFields: boolean = false
    canManageExpenses: boolean = false
    canAccessLeads: boolean = false
    canViewActivities: boolean = false
    canViewDashboardFront: boolean = false
    canViewDashboardManager: boolean = false

    largeMenu: Array<any> = []
    largeMenuBottom: Array<any> = []

    // Query-param-aware active matching so tabbed entries that share a route
    // (Clients vs Leads, both at /portal/clients) only highlight on their tab.
    clientsModuleActive: boolean = false
    private routerEventsSubscription!: Subscription
    mobileMenu: Array<any> | null = []
    settingsMenu: Array<any> | null = []
    reportMenuAction: any = {}

    isMobile!: boolean
    companyLabels = GeneralService.companyLabelsText
    sidenavtime: any

    reportsMenu: Array<any> = []
    businessSwitcherOpen: boolean = false

    constructor(
        private authService: AuthService,
        private alertService: AlertService,
        private _elementRef: ElementRef,
        private translation: TranslateService,
        private router: Router
    ) {}

    private updateClientsModuleActive(url: string): void {
        this.clientsModuleActive =
            url.startsWith('/portal/clients') || url.startsWith('/portal/leads')
    }

    getIsMobile() {
        const width = Math.max(
            document.documentElement.clientWidth,
            window.innerWidth || 0
        )
        return width <= 990
    }

    ngOnInit() {
        this.companyLabels = GeneralService.getCompanyLabels()

        this.updateIsMobile()
        window.addEventListener('resize', this.updateIsMobile.bind(this))

        // standalone
        GeneralService.customEvent.subscribe((event) => {
            if (event && event == CUSTOM_EVENTS.COMPANY_LABELS_CHANGED)
                this.updateHeaderLabels()
        })

        this.updateHeaderLabels()

        this.fillNavMenu()

        this.updateClientsModuleActive(this.router.url)

        this.routerEventsSubscription = this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.updateClientsModuleActive(event.urlAfterRedirects)
            })

        GeneralService.securityTagsDirectiveEvent.subscribe((res) => {
            if (res) this.fillNavMenu()
        })

        GeneralService.tenantChangeEvent.subscribe((res) => {
            this.fillNavMenu()
        })

        this.hasFPrint = FingerPrintService.isFingerPrintEnabled()
    }

    ngOnDestroy() {
        window.removeEventListener('resize', this.updateIsMobile.bind(this))
        if (this.routerEventsSubscription) {
            this.routerEventsSubscription.unsubscribe()
        }
    }

    updateIsMobile() {
        this.isMobile = this.getIsMobile()
    }

    sidenavOpen() {
        clearTimeout(this.sidenavtime)
        this.sidenavtime = setTimeout(() => {
            document.querySelector('.side-nav')?.classList.add('opensidenav')
        }, 350)
    }

    sidenavClose() {
        if (this.businessSwitcherOpen) return
        clearTimeout(this.sidenavtime)
        document.querySelector('.side-nav')?.classList.remove('opensidenav')
    }

    @HostListener('document:click', ['$event.target'])
    onDocumentClick(target: EventTarget | null) {
        const el = target as HTMLElement | null
        if (!el || !el.isConnected) return
        if (this._elementRef.nativeElement.contains(el)) return
        this.sidenavClose()
    }

    toggleNavigation() {
        this.toggleMenu.emit(true)
    }

    updateHeaderLabels() {
        this.companyLabels = GeneralService.getCompanyLabels()
        this.fillNavMenu()
    }

    triggerSettings() {
        this.hide = !this.hide
    }

    triggerMobileSettings() {
        this.hide3 = !this.hide3
    }

    triggerMobileNav() {
        this.hide2 = !this.hide2
    }

    handleClick(targetElement: any) {
        const clickedInside =
            this._elementRef.nativeElement.contains(targetElement)
        if (!clickedInside) {
            this.hide = true
            this.hide3 = true
        }
    }

    logout() {
        this.authService.logout()
    }

    hideSettings() {
        this.hide = true
        this.hide2 = true
        this.hide3 = true
    }

    onPanelToggled(isOpen: boolean) {
        this.businessSwitcherOpen = isOpen
    }

    branchChanged() {
        GeneralService.securityTagsDirectiveEvent.emit(true)
    }

    getCompanyName() {
        return localStorage.getItem(LOCAL_STORAG_KEYS.COMPANY_NAME)
    }

    openFPM() {
        let localServerAddress = localStorage.getItem(
            LOCAL_STORAG_KEYS.LOCAL_SERVER_ADDRESS
        )
        let localAccessWindow = localStorage.getItem(
            LOCAL_STORAG_KEYS.LOCAL_ACCESS_WINDOW
        )

        if (!localAccessWindow) {
            localAccessWindow = localStorage.getItem(
                LOCAL_STORAG_KEYS.TENANT_LOCAL_ACCESS_WINDOW
            )
        }

        if (localServerAddress != 'null') {
            let width = '790'
            let height = '440'
            window.open(
                'http://' +
                    localAccessWindow +
                    '/#/access/open?connection=' +
                    localServerAddress,
                'Fingerprint Access',
                'top=100, left=100, height=' + height + ', width=' + width
            )
            return true
        } else {
            this.alertService.error(
                'Error',
                'Local Server not found, could not connect.'
            )
            return false
        }
    }

    fillNavMenu() {
        this.canViewEvents =
            getBoolean(
                localStorage.getItem(LOCAL_STORAG_KEYS.EVENTS_ENABLED)
            ) === true

        this.canViewFields = this.authService.hasSecurityTag(
            SECURITY_TAGS.FIELDS_VIEW
        )

        this.canViewActivities = this.authService.hasSecurityTag(
            SECURITY_TAGS.ACTIVITIES_VIEW
        )

        this.canViewDashboardFront = this.authService.hasSecurityTag(
            SECURITY_TAGS.DASHBOARD_FRONT_DESK
        )

        this.canViewDashboardManager = this.authService.hasSecurityTag(
            SECURITY_TAGS.DASHBOARD_MANAGER
        )

        this.canManageExpenses = this.authService.hasSecurityTag(
            SECURITY_TAGS.MANAGE_EXPENSE
        )

        this.canAccessLeads = this.authService.hasSecurityTag(
            SECURITY_TAGS.ACCESS_LEAD_MODULE
        )

        this.reportMenuAction = null
        this.settingsMenu = null
        this.mobileMenu = null
        this.performFillNavMenu()
    }

    performFillNavMenu() {
        // DV-5499: the module list is shared with the portal home page.
        const modules = buildPortalModules({
            companyLabels: this.companyLabels,
            canViewEvents: this.canViewEvents,
            canViewActivities: this.canViewActivities,
            canViewFields: this.canViewFields,
            canViewDashboardManager: this.canViewDashboardManager,
            canAccessLeads: this.canAccessLeads,
            hasTenant: !!localStorage.getItem(LOCAL_STORAG_KEYS.TENANT_ID),
            isSuperUser: this.authService.userIsSuperUser()
        })
        const sidenavModules = modules.filter((module) =>
            module.surfaces.includes('sidenav')
        )

        this.largeMenu = sidenavModules.filter(
            (module) => module.section === 'main'
        )
        this.largeMenuBottom = sidenavModules.filter(
            (module) => module.section === 'bottom'
        )
    }
}
